import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import type { SharedBatchRequest } from '@reqshare/shared';
import type { ExpirationOption } from '@reqshare/shared';
import type { CapturedHttpRequest, CapturedHttpResponse, ConsoleEntry } from '@reqshare/protocol';
import { formatCurl } from '@reqshare/shared';
import { getBatchRequest, updateBatchExpiration, deleteBatchRequest } from '../../api/get-request';
import { useWebAuth } from '../../hooks/useWebAuth';
import { HeadersBlock } from '../../components/HeadersBlock/HeadersBlock';
import { BodyBlock } from '../../components/BodyBlock/BodyBlock';
import { ResponseBlock } from '../../components/ResponseBlock/ResponseBlock';
import { MetaInfo } from '../../components/MetaInfo/MetaInfo';
import { CopyButton } from '../../components/CopyButton/CopyButton';
import { OwnerMenu } from '../../components/OwnerMenu/OwnerMenu';
import './BatchRequestPage.scss';

const METHOD_COLORS: Record<string, string> = {
  GET: 'batch-entry__method--get',
  POST: 'batch-entry__method--post',
  PUT: 'batch-entry__method--put',
  PATCH: 'batch-entry__method--patch',
  DELETE: 'batch-entry__method--delete',
  HEAD: 'batch-entry__method--head',
  OPTIONS: 'batch-entry__method--options',
};

interface EntryDetailProps {
  request: CapturedHttpRequest;
  response?: CapturedHttpResponse;
  index: number;
}

function EntryDetail({ request, response, index }: EntryDetailProps) {
  const [expanded, setExpanded] = useState(false);
  const curlCommand = formatCurl(request);

  return (
    <section className="batch-entry">
      <button
        type="button"
        className="batch-entry__summary"
        onClick={() => setExpanded(!expanded)}
        aria-expanded={expanded}
      >
        <span className="batch-entry__index">{index + 1}</span>
        <span className={`batch-entry__method ${METHOD_COLORS[request.method] || ''}`}>
          {request.method}
        </span>
        <span className="batch-entry__url" title={request.url}>
          {request.url}
        </span>
        {response && (
          <span className={`batch-entry__status batch-entry__status--${Math.floor(response.status / 100)}xx`}>
            {response.status}
          </span>
        )}
        <span className={`batch-entry__chevron${expanded ? ' batch-entry__chevron--open' : ''}`}>
          &#9662;
        </span>
      </button>

      {expanded && (
        <div className="batch-entry__detail">
          <div className="batch-entry__curl-action">
            <CopyButton text={curlCommand} label="Copy as cURL" />
          </div>

          <div className="batch-entry__sections">
            <HeadersBlock title="Request Headers" headers={request.headers} />
            <BodyBlock title="Request Body" body={request.body} />
            {response && <ResponseBlock response={response} />}
          </div>
        </div>
      )}
    </section>
  );
}

const LEVEL_CLASSES: Record<string, string> = {
  error: 'console-log--error',
  warn: 'console-log--warn',
  info: 'console-log--info',
  log: 'console-log--log',
  debug: 'console-log--debug',
};

function formatTime(ts: number): string {
  const d = new Date(ts);
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit', fractionalSecondDigits: 3 });
}

function ConsoleLogsSection({ logs }: { logs: ConsoleEntry[] }) {
  return (
    <section className="batch-page__console">
      <h2 className="batch-page__console-title">
        Console Logs
        <span className="batch-page__console-count">{logs.length}</span>
      </h2>
      <div className="batch-page__console-list">
        {logs.map((log, i) => (
          <div key={i} className={`console-log ${LEVEL_CLASSES[log.level] || ''}`}>
            <span className="console-log__level">{log.level}</span>
            <span className="console-log__time">{formatTime(log.timestamp)}</span>
            <span className="console-log__args">{log.args.join(' ')}</span>
          </div>
        ))}
      </div>
    </section>
  );
}

export function BatchRequestPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { token, user } = useWebAuth();
  const [data, setData] = useState<SharedBatchRequest | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleted, setDeleted] = useState(false);

  useEffect(() => {
    if (!id) return;

    let cancelled = false;

    async function fetchData() {
      setLoading(true);
      setError(null);

      const result = await getBatchRequest(id!, token ?? undefined);

      if (cancelled) return;

      if (result.status === 404) {
        navigate('/not-found', { replace: true });
        return;
      }

      if (result.status === 410) {
        navigate('/expired', { replace: true });
        return;
      }

      if (result.error) {
        setError(result.error.message);
        setLoading(false);
        return;
      }

      setData(result.data!);
      setLoading(false);
    }

    fetchData();

    return () => {
      cancelled = true;
    };
  }, [id, navigate, token]);

  async function handleChangeExpiration(expiration: ExpirationOption) {
    if (!id || !token) return;
    try {
      const result = await updateBatchExpiration(id, expiration, token);
      setData((prev) =>
        prev ? { ...prev, expiresAt: result.expiresAt ?? undefined } : prev,
      );
    } catch (err) {
      console.error('Failed to update expiration:', err);
    }
  }

  async function handleDelete() {
    if (!id || !token) return;
    try {
      await deleteBatchRequest(id, token);
      setDeleted(true);
    } catch (err) {
      console.error('Failed to delete request:', err);
    }
  }

  if (deleted) {
    return (
      <div className="batch-page">
        <div className="batch-page__container">
          <div className="batch-page__deleted">
            <span className="batch-page__deleted-icon">&#10005;</span>
            <h2>Request Deleted</h2>
            <p>This shared request has been deleted by its author.</p>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="batch-page">
        <div className="batch-page__container">
          <div className="batch-page__loading">
            <div className="batch-page__spinner" />
            <p>Loading shared requests...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="batch-page">
        <div className="batch-page__container">
          <div className="batch-page__error">
            <h2>Something went wrong</h2>
            <p>{error}</p>
          </div>
        </div>
      </div>
    );
  }

  if (!data) {
    return null;
  }

  return (
    <div className="batch-page">
      <div className="batch-page__container">
        <div className="batch-page__count-bar">
          <span className="batch-page__count">
            {data.entries.length} request{data.entries.length !== 1 ? 's' : ''}
          </span>
        </div>

        <div className="batch-page__meta-row">
          <MetaInfo
            createdAt={data.createdAt}
            expiresAt={data.expiresAt}
            viewCount={data.viewCount}
          />
          {data.isOwner && (
            <OwnerMenu
              isPro={user?.subscription?.tier === 'pro'}
              onChangeExpiration={handleChangeExpiration}
              onDelete={handleDelete}
            />
          )}
        </div>

        <div className="batch-page__entries">
          {data.entries.map((entry, i) => (
            <EntryDetail
              key={i}
              request={entry.request}
              response={entry.response}
              index={i}
            />
          ))}
        </div>

        {data.consoleLogs && data.consoleLogs.length > 0 && (
          <ConsoleLogsSection logs={data.consoleLogs} />
        )}
      </div>
    </div>
  );
}
